/**
 * Auth store — Phase 4: JWT access token + refresh token.
 *
 * Session shape:
 *   token        — JWT access token (15min regular, 30d dev)
 *   refreshToken — UUID stored in D1 sessions table (30d)
 *   expiresAt    — Unix ms when access token expires
 *
 * Auto-refresh: proactive timer fires at 80% of token lifetime.
 * Reactive fallback: 401 on any API call triggers refresh + retry.
 *
 * Backward compat: old sessions without refreshToken still work —
 * the Worker's verifyToken falls back to session DB lookup.
 */
import { create } from 'zustand'
import { WORKER_URL } from '../api/worker'
import { startIdleTimer, stopIdleTimer } from '../services/idleTimer'

const SESSION_KEY = 'fom_auth_session'
const REFRESH_KEY = 'fom_refresh_token'

export interface FomSession {
  callSign: string
  rank: string
  employeeNo: string
  token: string
  refreshToken?: string
  expiresAt?: number
  name: string
  email: string
  phone: string
  accessLevel?: string
}

export function isExecRole(rank: string): boolean {
  const r = rank.toLowerCase()
  return (
    r === 'developer' || r === 'dv' ||
    r.includes('director') || r.includes('assistant') ||
    r.includes('lieut') || r.includes('lt.') ||
    r.includes('serg') || r.includes('sgt') ||
    r.includes('oic')
  )
}

export function isDeveloper(rank: string): boolean {
  const r = rank.toLowerCase()
  return r === 'developer' || r === 'dv'
}

export function getAccessLevel(session: FomSession): 'developer' | 'exec' | 'supervisor' | 'officer' {
  const r = session.rank.toLowerCase()
  if (r === 'developer' || r === 'dv') return 'developer'
  if (r.includes('director') || r.includes('assistant') || r.includes('lieut') || r.includes('lt.')) return 'exec'
  if (r.includes('serg') || r.includes('sgt') || r.includes('oic')) return 'supervisor'
  return 'officer'
}

let _refreshTimer: ReturnType<typeof setTimeout> | null = null

function clearRefreshTimer() {
  if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null }
}

function scheduleRefresh(expiresAt: number) {
  clearRefreshTimer()
  const now = Date.now()
  const lifetime = expiresAt - now
  if (lifetime <= 0) return
  // Refresh at 80% of lifetime (e.g., 12 min into a 15 min token)
  const delay = Math.max(lifetime * 0.8, 10_000)
  _refreshTimer = setTimeout(() => {
    useAuthStore.getState().refreshAccessToken()
  }, delay)
}

interface AuthState {
  session: FomSession | null
  refreshing: boolean
  hydrate: () => void
  setSession: (session: FomSession) => void
  signOut: () => void
  refreshAccessToken: () => Promise<boolean>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  refreshing: false,

  hydrate() {
    try {
      const raw = localStorage.getItem(SESSION_KEY)
      if (raw) {
        const s: FomSession = JSON.parse(raw)
        if (s.callSign && !s.callSign.includes('=')) {
          // Restore refresh token from separate key
          const rt = localStorage.getItem(REFRESH_KEY)
          if (rt) s.refreshToken = rt
          set({ session: s })
          // Schedule proactive refresh if we have expiry info
          if (s.expiresAt) scheduleRefresh(s.expiresAt)
          return
        }
      }
    } catch { /* silent */ }
    localStorage.removeItem(SESSION_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },

  setSession(session) {
    // Store refresh token separately (more secure — not in the main session blob)
    if (session.refreshToken) {
      localStorage.setItem(REFRESH_KEY, session.refreshToken)
    }
    // Don't persist refreshToken in the main session key
    const persist = { ...session }
    delete persist.refreshToken
    localStorage.setItem(SESSION_KEY, JSON.stringify(persist))
    set({ session })
    if (session.expiresAt) scheduleRefresh(session.expiresAt)
    // Start idle timer — sign out after 60 minutes of no activity
    startIdleTimer(() => { get().signOut() })
    // Broadcast session to all other open tabs (dashboard, desktop, etc.)
    try {
      const bc = new BroadcastChannel('castellan_auth')
      bc.postMessage({ type: 'SESSION_SET', session: persist })
      bc.close()
    } catch { /* silent — not supported in all envs */ }
  },

  signOut() {
    clearRefreshTimer()
    stopIdleTimer()
    // Fire-and-forget logout to revoke refresh token
    const s = get().session
    if (s?.refreshToken) {
      fetch(`${WORKER_URL}/api/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: s.refreshToken }),
      }).catch(() => {})
    }
    localStorage.removeItem(SESSION_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem('fom_last_posting_merge')
    set({ session: null, refreshing: false })
    // Broadcast sign-out to all other open tabs
    try {
      const bc = new BroadcastChannel('castellan_auth')
      bc.postMessage({ type: 'SESSION_CLEAR' })
      bc.close()
    } catch { /* silent */ }
  },

  async refreshAccessToken() {
    const { session, refreshing } = get()
    if (refreshing) return false
    const refreshToken = session?.refreshToken || localStorage.getItem(REFRESH_KEY) || ''
    if (!refreshToken) { get().signOut(); return false }

    set({ refreshing: true })
    try {
      const { workerPostV1 } = await import('../api/worker')
      const res = await workerPostV1<{
        success: boolean
        token?: string
        refreshToken?: string
        expiresIn?: number
        error?: string
      }>('/api/v1/auth/refresh', { refreshToken }, refreshToken)

      if (res.success && res.token) {
        const expiresAt = Date.now() + (res.expiresIn ?? 900) * 1000
        const updated: FomSession = {
          ...session!,
          token: res.token,
          refreshToken: res.refreshToken || refreshToken,
          expiresAt,
        }
        // Use setSession to persist + schedule next refresh
        get().setSession(updated)
        set({ refreshing: false })
        return true
      } else {
        get().signOut()
        return false
      }
    } catch {
      get().signOut()
      return false
    }
  },
}))

useAuthStore.getState().hydrate()

// Cross-tab auth sync via BroadcastChannel
// When you sign in on the desktop page, the dashboard page auto-signs in too.
// When you sign out, all tabs clear their session simultaneously.
if (typeof window !== 'undefined') {
  try {
    const bc = new BroadcastChannel('castellan_auth')
    bc.onmessage = (e) => {
      const { type, session } = e.data ?? {}
      if (type === 'SESSION_SET' && session) {
        // Another tab signed in — apply the session here without re-broadcasting
        const rt = localStorage.getItem(REFRESH_KEY)
        if (rt) session.refreshToken = rt
        useAuthStore.setState({ session })
        if (session.expiresAt) scheduleRefresh(session.expiresAt)
        startIdleTimer(() => useAuthStore.getState().signOut())
      } else if (type === 'SESSION_CLEAR') {
        // Another tab signed out — clear this tab's session too
        clearRefreshTimer()
        stopIdleTimer()
        useAuthStore.setState({ session: null, refreshing: false })
      }
    }
  } catch { /* BroadcastChannel not available in some envs */ }
}
